"use client";

import { useState, useMemo } from "react";
import {
  Eye,
  MapPin,
  Leaf,
  Sun,
  CloudSun,
  Cloud,
  ArrowRight,
  ArrowLeft,
  Check,
} from "lucide-react";
import { RecipeDetailsModal } from "./RecipeDetailsModal";
import {
  RECIPE_PLACEHOLDER_IMAGE,
  fallbackToRecipePlaceholder,
} from "@/lib/recipePlaceholder";
import type { RecipeAvailability } from "@/lib/cart/useRecipeAvailability";

export interface RecipePlantSummary {
  id: string;
  alias: string;
  imageUrl: string | null;
  price: number | null;
  shopifyVariantId?: string | null;
}

export interface Style {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  sunLevel: string;
  style?: string;
  orientation?: string;
  usableZones?: number[];
  genusAliasIds?: string[];
  genusAliases?: RecipePlantSummary[];
  retailerId?: string;
}

interface StylePickerProps {
  styles: Style[];
  selectedStyle?: string;
  onSelect: (styleId: string) => void;
  loading?: boolean;
  primaryColor?: string;
  userZone?: string | null;
  // Shopify-driven availability (empty when no data → renders exactly as before)
  availability?: Record<string, RecipeAvailability>;
  plantAvailability?: Record<string, boolean>;
  /**
   * The whole recipe card selects (like the sun-coverage cards) and the Select
   * button is dropped; Details stays. Used by the container flow.
   */
  selectOnCardClick?: boolean;
  /**
   * The caller asked the sun question itself and passes only matching styles
   * (container flow, PLA2-969): skip the sun sub-step and send its
   * "Sun Coverage" / "Change sun" buttons here instead.
   */
  onChangeSun?: () => void;
}

type SubStep = "sun-coverage" | "style-selection";

const SUN_COVERAGE_INFO: Record<
  string,
  { icon: React.ReactNode; label: string; hours: string; description: string }
> = {
  FULL_SUN: {
    icon: <Sun className="w-8 h-8" />,
    label: "Full Sun",
    hours: "6–8+ hours of direct sunlight",
    description:
      "South or west-facing areas with strong, sustained sun exposure. Ideal for many flowering grasses, shrubs & perennials.",
  },
  PART_SUN: {
    icon: <CloudSun className="w-8 h-8" />,
    label: "Part Sun",
    hours: "4–6 hours, preferably morning sun",
    description:
      "Needs closer to 6 hours or benefits from heat to bloom. Morning sun (east-facing) is gentler than intense afternoon exposure.",
  },
  PART_SUN_PART_SHADE: {
    icon: <CloudSun className="w-8 h-8" />,
    label: "Part Sun / Part Shade",
    hours: "3–6 hours of sun",
    description:
      "Prefers morning light with protection from intense afternoon sun (2–5 PM). Eastern exposures are ideal for these plants.",
  },
  PART_SHADE: {
    icon: <CloudSun className="w-8 h-8" />,
    label: "Part Shade",
    hours: "3–4 hours, avoid afternoon sun",
    description:
      "Needs protection from hot afternoon rays. Best on north or east-facing sides where sunlight is cooler and less direct.",
  },
  SHADE: {
    icon: <Cloud className="w-8 h-8" />,
    label: "Shade",
    hours: "Less than 3 hours of direct sunlight",
    description:
      "Thrives under tree canopy or north-facing areas with dappled or filtered light. Includes hostas, ferns, and astilbe.",
  },
  FULL_SHADE: {
    icon: <Cloud className="w-8 h-8" />,
    label: "Full Shade",
    hours: "No direct sunlight, dappled light only",
    description:
      "Filtered light through tree branches or consistently indirect light. Too much sun causes scorched, bleached, or browned leaves.",
  },
  DAPPLED_LIGHT: {
    icon: <Cloud className="w-8 h-8" />,
    label: "Dappled Light",
    hours: "Filtered sunlight through branches",
    description:
      "Sunlight that passes through a tree canopy creating shifting patterns of light and shade. Many shade plants thrive here.",
  },
};

function getSunInfo(sunLevel: string) {
  const key = sunLevel.toUpperCase();
  return (
    SUN_COVERAGE_INFO[key] || {
      icon: <Sun className="w-8 h-8" />,
      label: sunLevel
        .replace(/_/g, " ")
        .toLowerCase()
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" "),
      hours: "Varies",
      description: "Select this option to see matching garden styles.",
    }
  );
}

export function StylePicker({
  styles,
  selectedStyle,
  onSelect,
  loading = false,
  primaryColor = "#10b981",
  userZone,
  availability,
  plantAvailability,
  selectOnCardClick = false,
  onChangeSun,
}: StylePickerProps) {
  const [selectedRecipeForModal, setSelectedRecipeForModal] =
    useState<Style | null>(null);
  // Start with nothing selected — the user must actively pick a sun level (or
  // "All"), which advances to style selection. No default pre-selection.
  const [sunCoverageFilter, setSunCoverageFilter] = useState<string>(
    onChangeSun ? "all" : ""
  );
  const [subStep, setSubStep] = useState<SubStep>(
    onChangeSun ? "style-selection" : "sun-coverage"
  );
  const showSunStep = () =>
    onChangeSun ? onChangeSun() : setSubStep("sun-coverage");

  const formatZone = (zone: number | string) => {
    const zoneStr = zone.toString();
    return zoneStr.replace(/^ZONE_/i, "");
  };

  // Check if a recipe's usableZones match the user's zone
  const isZoneMatch = (style: Style): boolean => {
    if (!userZone || !style.usableZones || style.usableZones.length === 0)
      return true;
    const normalizedUserZone = userZone.toUpperCase().replace(/^ZONE_/i, "");
    return style.usableZones.some((z) => {
      const normalizedRecipeZone = z
        .toString()
        .toUpperCase()
        .replace(/^ZONE_/i, "");
      return normalizedRecipeZone === normalizedUserZone;
    });
  };

  // Filter styles by sun coverage, then sort: zone-matching first
  const filteredStyles = useMemo(() => {
    let result =
      sunCoverageFilter === "all"
        ? styles
        : styles.filter((style) => {
            const normalizedLevel = style.sunLevel.toUpperCase();
            return normalizedLevel === sunCoverageFilter.toUpperCase();
          });
    if (userZone) {
      result = [...result].sort((a, b) => {
        const aMatch = isZoneMatch(a) ? 0 : 1;
        const bMatch = isZoneMatch(b) ? 0 : 1;
        return aMatch - bMatch;
      });
    }
    // Push sold-out recipes to the end (stable) so in-stock styles come first.
    // No-op when availability is empty/undefined.
    if (availability) {
      result = [...result].sort((a, b) => {
        const aSoldOut = availability[a.id]?.soldOut === true ? 1 : 0;
        const bSoldOut = availability[b.id]?.soldOut === true ? 1 : 0;
        return aSoldOut - bSoldOut;
      });
    }
    return result;
  }, [styles, sunCoverageFilter, userZone, availability]);

  // Get unique sun levels for filter
  const sunLevels = useMemo(() => {
    const levels = new Set(styles.map((s) => s.sunLevel.toUpperCase()));
    return Array.from(levels).sort();
  }, [styles]);

  const handleSunCoverageSelect = (level: string) => {
    setSunCoverageFilter(level);
    setSubStep("style-selection");
  };

  if (loading) {
    return (
      <div className="space-y-4 px-4 md:px-0">
        <h2 className="text-xl md:text-2xl font-bold text-plantista-950 dark:text-white font-display">
          Input Preferences
        </h2>
        <p className="text-sm md:text-base text-plantista-600 dark:text-plantista-300">
          Loading available garden styles...
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[4/3] bg-gray-200 dark:bg-plantista-800 rounded-lg mb-2"></div>
              <div className="h-4 bg-gray-200 dark:bg-plantista-800 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 dark:bg-plantista-800 rounded w-full"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (styles.length === 0) {
    return (
      <div className="space-y-4 px-4 md:px-0">
        <h2 className="text-xl md:text-2xl font-bold text-plantista-950 dark:text-white font-display">
          Input Preferences
        </h2>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 md:p-8 text-center">
          <p className="text-yellow-800 dark:text-yellow-400 font-medium">
            No garden styles available
          </p>
          <p className="text-yellow-600 dark:text-yellow-300 text-sm mt-2">
            Please configure recipes in your retailer dashboard or check your
            API connection.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4 px-4 md:px-0">
        <h2 className="text-xl md:text-2xl font-bold text-plantista-950 dark:text-white font-display">
          Input Preferences
        </h2>

        {/* Sub-step indicator */}
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={showSunStep}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all font-medium ${
              subStep === "sun-coverage"
                ? "text-plantista-950 shadow-md"
                : sunCoverageFilter !== "all"
                  ? "bg-plantista-100 dark:bg-plantista-800 text-plantista-700 dark:text-plantista-300 hover:bg-plantista-200 dark:hover:bg-plantista-700"
                  : "bg-gray-100 dark:bg-plantista-900 text-gray-500 dark:text-plantista-500"
            }`}
            style={
              subStep === "sun-coverage"
                ? { backgroundColor: primaryColor }
                : undefined
            }
          >
            <Sun className="w-3.5 h-3.5" />
            <span>Sun Coverage</span>
            {sunCoverageFilter !== "all" && subStep !== "sun-coverage" && (
              <Check className="w-3.5 h-3.5 text-plantista-500" />
            )}
          </button>
          <ArrowRight className="w-4 h-4 text-gray-400 dark:text-plantista-600" />
          <button
            onClick={() => {
              if (sunCoverageFilter !== "all" || styles.length > 0) {
                setSubStep("style-selection");
              }
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all font-medium ${
              subStep === "style-selection"
                ? "text-plantista-950 shadow-md"
                : selectedStyle
                  ? "bg-plantista-100 dark:bg-plantista-800 text-plantista-700 dark:text-plantista-300 hover:bg-plantista-200 dark:hover:bg-plantista-700"
                  : "bg-gray-100 dark:bg-plantista-900 text-gray-500 dark:text-plantista-500"
            }`}
            style={
              subStep === "style-selection"
                ? { backgroundColor: primaryColor }
                : undefined
            }
          >
            <Leaf className="w-3.5 h-3.5" />
            <span>Style</span>
            {selectedStyle && subStep !== "style-selection" && (
              <Check className="w-3.5 h-3.5 text-plantista-500" />
            )}
          </button>
        </div>

        {/* Sub-step A: Sun Coverage Selection */}
        {subStep === "sun-coverage" && (
          <div className="space-y-3">
            <p className="text-sm md:text-base text-plantista-600 dark:text-plantista-300">
              How much sunlight does your planting area receive?
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* "All" option */}
              <button
                onClick={() => handleSunCoverageSelect("all")}
                className={`relative flex items-start gap-4 p-4 rounded-xl border-2 transition-all text-left group ${
                  sunCoverageFilter === "all"
                    ? "border-plantista-500 dark:border-plantista-400 bg-plantista-50 dark:bg-plantista-900/50 ring-1 ring-plantista-500/30"
                    : "border-gray-200 dark:border-plantista-800 bg-gray-50 dark:bg-plantista-900 hover:border-plantista-300 dark:hover:border-plantista-600 hover:shadow-md"
                }`}
              >
                {sunCoverageFilter === "all" && (
                  <div className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full flex items-center justify-center bg-plantista-700 dark:bg-plantista-300">
                    <Check className="w-3 h-3 text-white dark:text-plantista-900" />
                  </div>
                )}
                <div
                  className={`p-2.5 rounded-lg shrink-0 ${
                    sunCoverageFilter === "all"
                      ? "bg-plantista-100 dark:bg-plantista-800 text-plantista-600 dark:text-plantista-300"
                      : "bg-gray-100 dark:bg-plantista-800/50 text-gray-500 dark:text-plantista-400 group-hover:bg-plantista-100 dark:group-hover:bg-plantista-800 group-hover:text-plantista-600"
                  }`}
                >
                  <Sun className="w-8 h-8" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-plantista-950 dark:text-white text-sm">
                    All Sun Levels
                  </h3>
                  <p className="text-xs text-plantista-500 dark:text-plantista-400 mt-0.5 font-medium">
                    Show all options
                  </p>
                  <p className="text-xs text-plantista-600 dark:text-plantista-400 mt-1 leading-relaxed">
                    Browse all {styles.length} available garden styles
                    regardless of sun exposure.
                  </p>
                </div>
              </button>

              {sunLevels.map((level) => {
                const info = getSunInfo(level);
                const count = styles.filter(
                  (s) => s.sunLevel.toUpperCase() === level
                ).length;
                const isSelected = sunCoverageFilter.toUpperCase() === level;

                return (
                  <button
                    key={level}
                    onClick={() => handleSunCoverageSelect(level)}
                    className={`relative flex items-start gap-4 p-4 rounded-xl border-2 transition-all text-left group ${
                      isSelected
                        ? "border-plantista-500 dark:border-plantista-400 bg-plantista-50 dark:bg-plantista-900/50 ring-1 ring-plantista-500/30"
                        : "border-gray-200 dark:border-plantista-800 bg-gray-50 dark:bg-plantista-900 hover:border-plantista-300 dark:hover:border-plantista-600 hover:shadow-md"
                    }`}
                  >
                    {isSelected && (
                      <div
                        className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <div
                      className={`p-2.5 rounded-lg shrink-0 ${
                        isSelected
                          ? "bg-plantista-100 dark:bg-plantista-800 text-plantista-600 dark:text-plantista-300"
                          : "bg-gray-100 dark:bg-plantista-800/50 text-gray-500 dark:text-plantista-400 group-hover:bg-plantista-100 dark:group-hover:bg-plantista-800 group-hover:text-plantista-600"
                      }`}
                    >
                      {info.icon}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-plantista-950 dark:text-white text-sm">
                        {info.label}
                      </h3>
                      <p className="text-xs text-plantista-500 dark:text-plantista-400 mt-0.5 font-medium">
                        {info.hours}
                      </p>
                      <p className="text-xs text-plantista-600 dark:text-plantista-400 mt-1 leading-relaxed">
                        {info.description}
                      </p>
                      <span className="inline-block mt-2 text-xs font-medium text-plantista-500 dark:text-plantista-400 bg-plantista-50 dark:bg-plantista-800 px-2 py-0.5 rounded-full">
                        {count} style{count !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Sub-step B: Style Selection */}
        {subStep === "style-selection" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm md:text-base text-plantista-600 dark:text-plantista-300">
                Choose a design style that matches your vision
              </p>
              <button
                onClick={showSunStep}
                className="flex items-center gap-1 text-sm text-plantista-600 dark:text-plantista-400 hover:text-plantista-800 dark:hover:text-plantista-200 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Change sun</span>
              </button>
            </div>

            {/* Active filter pills */}
            {(sunCoverageFilter !== "all" || userZone) && (
              <div className="flex items-center gap-2 flex-wrap">
                {userZone && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700">
                    <MapPin className="w-3 h-3" />
                    Zone {userZone.replace(/^ZONE_/i, "")}
                  </span>
                )}
                {sunCoverageFilter !== "all" && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-plantista-100 dark:bg-plantista-800 text-plantista-700 dark:text-plantista-300 border border-plantista-200 dark:border-plantista-700">
                    {getSunInfo(sunCoverageFilter).icon && (
                      <Sun className="w-3 h-3" />
                    )}
                    {getSunInfo(sunCoverageFilter).label}
                    <button
                      onClick={() => setSunCoverageFilter("all")}
                      aria-label="Clear sun coverage filter"
                      className="ml-1 text-plantista-500 hover:text-plantista-700 dark:hover:text-plantista-200"
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </span>
                )}
                <span className="text-xs text-gray-500 dark:text-plantista-500">
                  {filteredStyles.length} style
                  {filteredStyles.length !== 1 ? "s" : ""}
                </span>
              </div>
            )}

            {filteredStyles.length === 0 ? (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
                <p className="text-yellow-800 dark:text-yellow-400 font-medium">
                  No styles match this sun coverage filter
                </p>
                <p className="text-yellow-600 dark:text-yellow-300 text-sm mt-2">
                  Try selecting a different sun coverage option.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {filteredStyles.map((style) => {
                  const zoneDisabled = userZone ? !isZoneMatch(style) : false;
                  const styleAvailability = availability?.[style.id];
                  const isSoldOut = styleAvailability?.soldOut === true;
                  const disabled = zoneDisabled || isSoldOut;
                  return (
                    <div
                      key={style.id}
                      {...(selectOnCardClick
                        ? {
                            role: "button",
                            tabIndex: disabled ? -1 : 0,
                            "aria-disabled": disabled || undefined,
                            "aria-pressed": selectedStyle === style.id,
                            "aria-label": `Choose ${style.name}`,
                            onClick: () => !disabled && onSelect(style.id),
                            onKeyDown: (e: React.KeyboardEvent) => {
                              if (e.target !== e.currentTarget || disabled)
                                return;
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onSelect(style.id);
                              }
                            },
                          }
                        : {})}
                      className={`
                    relative overflow-hidden rounded-lg border-2 transition-all bg-white dark:bg-plantista-900 flex flex-col h-full
                    ${selectOnCardClick ? (disabled ? "cursor-not-allowed" : "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-plantista-500 active:scale-[0.99]") : ""}
                    ${zoneDisabled ? "opacity-60 grayscale-[40%]" : ""}
                    ${isSoldOut && !zoneDisabled ? "opacity-75" : ""}
                    ${
                      selectedStyle === style.id
                        ? "border-plantista-500 dark:border-plantista-400 ring-2 ring-plantista-500 dark:ring-plantista-400"
                        : disabled
                          ? "border-gray-300 dark:border-plantista-800"
                          : "border-gray-200 dark:border-plantista-800 hover:border-plantista-500 dark:hover:border-plantista-400"
                    }
                  `}
                    >
                      {/* Zone mismatch badge (top-left) */}
                      {zoneDisabled && (
                        <div className="absolute top-2 left-2 z-10 bg-amber-100 dark:bg-amber-900/80 text-amber-800 dark:text-amber-300 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-amber-300 dark:border-amber-700 flex items-center gap-1">
                          <MapPin className="w-2.5 h-2.5" />
                          Outside Zone {userZone?.replace(/^ZONE_/i, "")}
                        </div>
                      )}
                      {/* Sold-out badge (top-right, minimalist monochrome) */}
                      {isSoldOut && (
                        <div className="absolute top-2 right-2 z-10 bg-stone-900/85 dark:bg-white/90 text-white dark:text-stone-900 backdrop-blur-sm text-[11px] font-medium tracking-wide px-2.5 py-1 rounded-full">
                          Sold out
                        </div>
                      )}
                      <div className="h-48 relative shrink-0">
                        <img
                          src={style.imageUrl || RECIPE_PLACEHOLDER_IMAGE}
                          alt={style.name}
                          className={`w-full h-full object-cover ${isSoldOut ? "grayscale" : ""}`}
                          onError={fallbackToRecipePlaceholder}
                        />
                        {selectedStyle === style.id && (
                          <div className="absolute inset-0 bg-plantista-500/60 dark:bg-plantista-400/60 flex items-center justify-center">
                            <div className="bg-white rounded-full p-2 shadow-lg">
                              <svg
                                className="w-6 h-6 text-plantista-600"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="p-3 text-left space-y-2 flex flex-col flex-1">
                        <div className="flex-1">
                          <h3 className="font-semibold text-plantista-950 dark:text-white text-sm leading-tight">
                            {style.name}
                          </h3>
                          <p className="text-xs text-plantista-600 dark:text-plantista-300 mt-1 line-clamp-1">
                            {style.description}
                          </p>

                          <div className="mt-1 flex flex-wrap items-start gap-x-2 gap-y-1 text-xs text-plantista-600 dark:text-plantista-400">
                            {style.usableZones &&
                              style.usableZones.length > 0 && (
                                <span
                                  className={`flex items-start space-x-1 ${zoneDisabled ? "text-amber-600 dark:text-amber-400" : ""}`}
                                >
                                  <MapPin
                                    className={`w-3 h-3 mt-0.5 shrink-0 ${zoneDisabled ? "text-amber-500" : "text-yellow-500"}`}
                                  />
                                  <span>
                                    {style.usableZones
                                      .map(formatZone)
                                      .join(", ")}
                                  </span>
                                </span>
                              )}
                            {style.genusAliases &&
                              style.genusAliases.length > 0 && (
                                <span className="flex items-center space-x-1">
                                  <Leaf className="w-3 h-3 text-plantista-500" />
                                  <span>
                                    {style.genusAliases.length} plants
                                  </span>
                                </span>
                              )}
                          </div>
                          {isSoldOut && styleAvailability && (
                            <p className="mt-1 text-[11px] text-stone-500 dark:text-plantista-400">
                              {styleAvailability.available} of{" "}
                              {styleAvailability.reported} plants in stock
                            </p>
                          )}
                        </div>
                        <div className="flex space-x-1.5 mt-auto">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRecipeForModal(style);
                            }}
                            type="button"
                            className="flex-1 min-h-[36px] px-2 py-1.5 text-xs border border-gray-300 dark:border-plantista-700 rounded hover:bg-gray-50 dark:hover:bg-plantista-800 text-gray-600 dark:text-plantista-300 transition flex items-center justify-center space-x-1"
                          >
                            <Eye className="w-3 h-3" />
                            <span>Details</span>
                          </button>
                          {!selectOnCardClick && (
                            <button
                              onClick={() => !disabled && onSelect(style.id)}
                              disabled={disabled}
                              className={`flex-1 px-2 py-1.5 text-xs rounded transition font-bold ${
                                disabled
                                  ? "bg-gray-200 dark:bg-plantista-800 text-gray-400 dark:text-plantista-600 cursor-not-allowed"
                                  : "text-plantista-950"
                              }`}
                              style={
                                !disabled
                                  ? {
                                      backgroundColor:
                                        selectedStyle === style.id
                                          ? "#6b7280"
                                          : primaryColor,
                                    }
                                  : undefined
                              }
                            >
                              {zoneDisabled
                                ? "N/A"
                                : isSoldOut
                                  ? "Sold out"
                                  : selectedStyle === style.id
                                    ? "✓"
                                    : "Select"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <RecipeDetailsModal
        recipe={selectedRecipeForModal}
        isOpen={!!selectedRecipeForModal}
        onClose={() => setSelectedRecipeForModal(null)}
        onSelect={onSelect}
        isSelected={
          selectedRecipeForModal
            ? selectedStyle === selectedRecipeForModal.id
            : false
        }
        primaryColor={primaryColor}
        plantAvailability={plantAvailability}
        soldOut={
          selectedRecipeForModal
            ? availability?.[selectedRecipeForModal.id]?.soldOut === true
            : false
        }
      />
    </>
  );
}
